import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ShieldCheck, Loader2, FileSignature, AlertTriangle, Download } from "lucide-react";

export default function ConsentPublic() {
  const { token } = useParams();
  const [consent, setConsent] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/public/consent/${token}`); setConsent(r.data); setStatus("ready"); }
    catch { setStatus("error"); }
  };
  useEffect(() => { load(); }, [token]);

  const sign = async () => {
    if (!name.trim()) return;
    setSigning(true);
    try { await axios.post(`${API}/public/consent/${token}/sign`, { signed_name: name }); await load(); }
    catch { /* noop */ }
    finally { setSigning(false); }
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[#F4FBF7]"><Loader2 className="h-6 w-6 animate-spin text-[#00A85A]" /></div>;
  if (status === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4FBF7] p-6">
      <div className="max-w-sm text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 mb-4"><AlertTriangle className="h-7 w-7 text-red-500" /></div><h1 className="text-lg font-bold text-[#101516]">This consent link is invalid</h1><p className="text-sm text-[#687173] mt-1">Please contact your clinic for a new link.</p></div>
    </div>
  );

  const signed = consent.status === "signed";
  return (
    <div className="min-h-screen bg-[#F4FBF7] py-8 px-4">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-t-2xl bg-[#00A85A] px-6 py-5 text-white">
          <div className="text-lg font-extrabold">{consent.clinic_name}</div>
          <div className="text-sm opacity-90">Informed Treatment Consent</div>
        </div>
        <div className="rounded-b-2xl bg-white p-6 shadow-sm" data-testid="consent-public-card">
          <div className="flex items-center gap-2 mb-1"><FileSignature className="h-5 w-5 text-[#00A85A]" /><h1 className="text-lg font-bold text-[#101516]">{consent.title}</h1></div>
          <p className="text-sm text-[#687173] mb-5">Patient: <b>{consent.patient_name}</b>{consent.doctor_name ? ` · Clinician: ${consent.doctor_name}` : ""}</p>

          <div className="space-y-5">
            {(consent.sections || []).map((s, i) => (
              <div key={i}>
                <div className="text-sm font-bold text-[#101516] mb-1.5">{s.heading}</div>
                {s.text && <p className="text-sm text-[#3a4a4f] leading-relaxed">{s.text}</p>}
                {(s.items || []).length > 0 && <ul className="space-y-1">{s.items.map((it, x) => <li key={x} className="flex gap-2 text-sm text-[#3a4a4f]"><span className="text-[#00A85A]">•</span>{it}</li>)}</ul>}
              </div>
            ))}
          </div>

          {(consent.signatures?.doctor || consent.signatures?.patient) && (
            <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="consent-public-signatures">
              {[["doctor", "Doctor"], ["patient", "Patient"]].map(([r, l]) => (
                <div key={r} className="rounded-xl border border-[#E2E8F0] p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{l} signature</div>
                  {consent.signatures?.[r] ? <><img src={consent.signatures[r].data_url} alt={`${l} signature`} className="mt-1 h-20" /><div className="text-xs text-[#687173]">{consent.signatures[r].name} · {new Date(consent.signatures[r].signed_at).toLocaleDateString()}</div></> : <div className="mt-1 h-20 text-xs text-[#8B9698]">Not signed yet</div>}
                </div>
              ))}
            </div>
          )}
          <a href={`${API}/public/consent/${token}/pdf`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#00A85A] hover:underline" data-testid="consent-public-pdf"><Download className="h-4 w-4" />Download PDF</a>
          <div className="mt-7 border-t border-[#E2E8F0] pt-6">
            {signed ? (
              <div className="rounded-xl border border-[#00A85A]/30 bg-[#F4FBF7] p-4 flex items-start gap-3" data-testid="consent-signed-state">
                <CheckCircle2 className="h-5 w-5 text-[#00A85A] shrink-0 mt-0.5" />
                <div><div className="text-sm font-bold text-[#008F4D]">Consent signed</div><div className="text-xs text-[#687173]">Signed by {consent.signed_name}{consent.signed_at ? ` on ${new Date(consent.signed_at).toLocaleDateString()}` : ""}. Thank you.</div></div>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="flex items-start gap-2.5 text-sm text-[#3a4a4f] cursor-pointer">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#00A85A]" data-testid="consent-agree" />
                  <span>I have read and understood the information above and give my informed consent to proceed with the treatment.</span>
                </label>
                <div className="space-y-1.5"><Label>Type your full name to sign</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="consent-name-input" /></div>
                <Button onClick={sign} disabled={!agree || !name.trim() || signing} data-testid="consent-sign-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">{signing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}Sign consent</Button>
              </div>
            )}
          </div>
          <p className="mt-6 text-center text-[11px] text-[#8B9698]">Powered by CONTAHA · This is a secure clinical consent document.</p>
        </div>
      </div>
    </div>
  );
}
